import { useNavigate } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { startTransition } from 'react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { SectionLabel } from '@/components/custom/section-label'
import { IndicatorIcon } from './indicator-icon'
import { indicatorPalette } from './indicator-colors'

type SelectOption = 'competences' | 'values' | 'personality'

// Build the 5 button states (active / inactive / disabled / standard /
// standardActive) from the central indicator palette so colour edits flow
// through here automatically.
const DISABLED_STATE = 'border border-transparent bg-muted/50 text-muted-foreground/50 cursor-not-allowed'

const buildSelectState = (key: SelectOption) => {
  const p = indicatorPalette[key]
  return {
    active: cn(p.activeBg, p.activeText, 'border', p.activeBorder),
    inactive: cn('bg-muted border-transparent text-muted-foreground', p.hoverBg, p.hoverText),
    disabled: DISABLED_STATE,
    standard: cn('bg-transparent text-muted-foreground border border-dashed', p.hoverBorder, p.hoverText),
    standardActive: cn(p.activeBg, p.activeText, 'border border-dashed', p.activeBorderStrong),
  }
}

const selectColors: Record<SelectOption, ReturnType<typeof buildSelectState>> = {
  competences: buildSelectState('competences'),
  values: buildSelectState('values'),
  personality: buildSelectState('personality'),
}

function SelectButton(props: {
  selectKey: SelectOption
  currentSelect: SelectOption
  icon: React.ReactNode
  disabled?: boolean
  disabledReason?: string
  /** Standard profile: clickable but shows border-only style to indicate not fully described */
  isStandard?: boolean
  standardTooltip?: string
  /** If provided, also set 'selected' search param when clicking */
  selectedSlot?: 'first' | 'second'
}) {
  const { t } = useLingui()
  const navigate = useNavigate()

  const labels: Record<SelectOption, string> = {
    competences: t`Competences`,
    values: t`Values`,
    personality: t`Personality`,
  }

  const isActive = props.selectKey === props.currentSelect
  const colors = selectColors[props.selectKey]

  // Toggle behavior: if active, clear to default (competences via undefined)
  // If inactive, set to this select option
  const getNextSelect = () => {
    if (isActive) {
      // Clicking active button deselects - return to default (competences)
      return props.selectKey === 'competences' ? undefined : 'competences'
    }
    // Clicking inactive button selects it
    return props.selectKey === 'competences' ? undefined : props.selectKey
  }

  const buttonContent = (
    <>
      {props.icon}
      <span>{labels[props.selectKey]}</span>
    </>
  )

  // If disabled, render as a span with tooltip
  if (props.disabled) {
    const button = (
      <span className={cn('flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium', colors.disabled)}>
        {buttonContent}
      </span>
    )

    if (props.disabledReason) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-[200px] text-xs">
            {props.disabledReason}
          </TooltipContent>
        </Tooltip>
      )
    }

    return button
  }

  // Determine the style based on state
  const getButtonStyle = () => {
    if (props.isStandard) {
      // Standard profile: border-only style (hollow)
      return isActive ? colors.standardActive : colors.standard
    }
    // Normal style
    return isActive ? colors.active : colors.inactive
  }

  const buildSearch = (prev: Record<string, unknown>) => ({
    ...prev,
    type: getNextSelect(),
    ...(props.selectedSlot ? { selected: props.selectedSlot } : {}),
  })

  // Drive navigation via startTransition. Changing `type` re-renders the entire
  // report tree (~90ms long task otherwise); marking it as a transition keeps
  // the click responsive and the heavy reconcile interruptible.
  // We use <button> (not <Link>) because this control updates the current
  // route's search params — it doesn't navigate to a different route, so the
  // button semantic is correct and avoids Link's internal sync click handler.
  const handleClick = () => {
    startTransition(() => {
      navigate({ to: '.', search: buildSearch as never })
    })
  }

  const link = (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isActive}
      className={cn(
        'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors cursor-pointer',
        getButtonStyle(),
      )}
    >
      {buttonContent}
    </button>
  )

  // If standard profile, wrap with tooltip to explain
  if (props.isStandard && props.standardTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px] text-xs">
          {props.standardTooltip}
        </TooltipContent>
      </Tooltip>
    )
  }

  return link
}

// Completion status for determining which buttons should be disabled or shown as standard
// For jobs: `false` means standard profile (show with border), `true` means fully described
// For applicants: `false` means not completed (disabled), `true` means completed
interface CompletionStatus {
  competences?: boolean | null
  values?: boolean | null
  personality?: boolean | null
}

export function SelectByButtons(props: {
  currentSelect: SelectOption
  // Optional completion status to enable/disable buttons based on test completion
  completionStatus?: CompletionStatus
  // Optional custom disabled reasons per type
  disabledReasons?: Partial<Record<SelectOption, string>>
  // If true, treat false values as "standard profile" (clickable with border) instead of disabled
  // This is used for offers where standard profiles are always available
  isOfferProfile?: boolean
  // Custom tooltip for standard profile buttons
  standardProfileTooltip?: string
  // If true, show "Select by" label above the buttons
  showLabel?: boolean
  // If provided, also set 'selected' search param when clicking (for compare mode)
  selectedSlot?: 'first' | 'second'
}) {
  const { t } = useLingui()

  // Default disabled reason
  const defaultDisabledReason = t`Test not completed`
  const defaultStandardTooltip = props.standardProfileTooltip ?? t`Using standard profile`

  // For offers: false means standard profile (clickable), for applicants: false means disabled
  const getButtonState = (completed: boolean | null | undefined) => {
    if (completed === true) return 'completed'
    if (completed === false) {
      return props.isOfferProfile ? 'standard' : 'disabled'
    }
    return 'completed' // null/undefined = assume available
  }

  const competencesState = getButtonState(props.completionStatus?.competences)
  const valuesState = getButtonState(props.completionStatus?.values)
  const personalityState = getButtonState(props.completionStatus?.personality)

  return (
    <div className="flex flex-col gap-1">
      {props.showLabel && <SectionLabel>{t`Select`}</SectionLabel>}
      <div className="flex flex-wrap gap-1.5">
        <SelectButton
          selectKey="competences"
          currentSelect={props.currentSelect}
          icon={<IndicatorIcon kind="competences" inherit />}
          disabled={competencesState === 'disabled'}
          isStandard={competencesState === 'standard'}
          disabledReason={props.disabledReasons?.competences ?? defaultDisabledReason}
          standardTooltip={defaultStandardTooltip}
          selectedSlot={props.selectedSlot}
        />
        <SelectButton
          selectKey="values"
          currentSelect={props.currentSelect}
          icon={<IndicatorIcon kind="values" inherit />}
          disabled={valuesState === 'disabled'}
          isStandard={valuesState === 'standard'}
          disabledReason={props.disabledReasons?.values ?? defaultDisabledReason}
          standardTooltip={defaultStandardTooltip}
          selectedSlot={props.selectedSlot}
        />
        <SelectButton
          selectKey="personality"
          currentSelect={props.currentSelect}
          icon={<IndicatorIcon kind="personality" inherit />}
          disabled={personalityState === 'disabled'}
          isStandard={personalityState === 'standard'}
          disabledReason={props.disabledReasons?.personality ?? defaultDisabledReason}
          standardTooltip={defaultStandardTooltip}
          selectedSlot={props.selectedSlot}
        />
      </div>
    </div>
  )
}
export type { SelectOption, CompletionStatus }
